/**
 * The ONLY place the working title lives. Trademark is not cleared, so it must
 * never leak into asset filenames, save keys, or telemetry ids. Use the opaque
 * STORAGE_NAMESPACE / ARTIFACT_SLUG for anything durable.
 */
export const GAME_NAME = "Grudgefire";

/** Opaque prefix for persisted keys. Deliberately NOT derived from GAME_NAME. */
export const STORAGE_NAMESPACE = "gf";

/** Build artifact filename stem (zips). Read by scripts/zip-dist.mjs. */
export const ARTIFACT_SLUG = "grudgefire";

/**
 * Google sign-in on the game's own domain (B3.10.1, D-077; D-116): the Web client "Grudgefire" of
 * the owner's Google Cloud project `gfstudio-playtest` (N36). The client ID is public and its
 * secret is never used: the page asks for an ID token alone. The Worker reads the same ID from
 * `server/wrangler.jsonc` (`test/deploys.test.ts`).
 */
export const GOOGLE_SIGN_IN = {
  clientId: "250314813886-3j5d37bg1dirr2fovscpe1unq113t34m.apps.googleusercontent.com",
  authUrl: "https://accounts.google.com/o/oauth2/v2/auth",
  /**
   * The origins whose "/" the client lists as an authorized redirect URI, matched exactly by
   * Google: the game's domain, `pnpm dev` and `pnpm preview`. The pages.dev address and its
   * preview deploys are not listed since D-116, so no chip shows there.
   */
  redirectOrigins: ["https://grudgefire.com", "http://localhost:5173", "http://localhost:4173"],
} as const;

/**
 * AN1 (D-122): which portal served a page, by the end of its host, the first match winning (the
 * game's domain, the Pages project, itch.io's frame, CrazyGames' frame, this machine); any other
 * host is "other". A host matches its suffix whole or as a subdomain of it.
 */
export const PORTAL_HOSTS = [
  ["grudgefire.com", "site"],
  ["gfstudio-playtest.pages.dev", "pages"],
  ["itch.zone", "itch"],
  ["itch.io", "itch"],
  ["crazygames.com", "crazygames"],
  ["localhost", "local"],
  ["127.0.0.1", "local"],
] as const;

/**
 * D-163: the picture and the address a link to the game shows where it is shared (Reddit, Discord,
 * a chat): `public/share-card.png`, made by `scripts/share-card.mjs`, at the 1200 by 630 that
 * Open Graph readers crop the least. Crawlers want an absolute address, so it is the domain's.
 */
export const SHARE_CARD = {
  site: "https://grudgefire.com/",
  image: "share-card.png",
  width: 1200,
  height: 630,
} as const;

/**
 * How the King's seat is played (Increment CC1): the court of ADR-0011 ("classic"), or City
 * Command ("cities"): his cities train and march by themselves and he commands for free.
 */
export type ControlScheme = "classic" | "cities";

/** Design tunables. Gameplay increments will grow this; keep it flat and typed. */
export const TUNABLES = {
  /** Target run length in seconds. */
  runLengthSec: 8 * 60,
  /** Core ticks per wall-clock second; the render ticker converts elapsed ms into ticks. */
  ticksPerSecond: 4,
  /** Largest wall-clock gap fed to the ticker per frame, so a backgrounded tab does not fast-forward. */
  maxFrameMs: 250,
  /**
   * Save and resume (L1.1, docs/INCREMENT-L1.1-REPORT.md). The run in progress is written to
   * the platform's data store every `intervalTicks` (10 s at 4 ticks/s) and whenever the tab
   * is hidden or unloaded; a run younger than one interval is not kept (no prompt for a run
   * left within ten seconds). A shipped duel at tick 656 is 14 KB of JSON (test/save.test.ts).
   */
  save: {
    intervalTicks: 40,
  },
  /**
   * Unit rules (Increment 4, ADR-0010, docs/design/UNITS-PIVOT-IDEA.md). Tiles never fight:
   * land pays supply into a faction pool, cities build units from it, units take land. All
   * integers. Every number is est. until the Increment 4 report's gates say otherwise.
   */
  rules: {
    /** Supply, wall regrowth, healing and capture-by-standing every 2 s at 4 ticks/s. */
    productionIntervalTicks: 8,
    /** A unit takes one move step (its `move` hexes) every 3 s: at 2 s the script ended runs in two minutes (Increment 4 report). */
    moveIntervalTicks: 12,
    /** Every unit with an enemy in range strikes once a second. */
    combatIntervalTicks: 4,
    /**
     * A unit takes the wall-free foreign hex it has stood on this long (2 s): one production
     * interval, the longest wait under the old "at the next pay tick" rule. est.
     */
    captureTicks: 8,
    /** Both pools start here: one Spear, at once. */
    startSupply: 10,
    /** HP a unit regains per production interval with no enemy unit adjacent. */
    healPerInterval: 1,
    /**
     * The levy (A26, docs/INCREMENT-5.4-AMENDMENTS.md): a unit costs its base price times
     * (100 + 50 n + 25 n * n) percent, n the units fielded or training: a Spear is 6, 10, 18,
     * 28, 42 ... and 153 as the ninth. A side with no army trains at half the build time.
     * Measured over 80 seeds with leads counted from tick 200 (A30): lead changes 0.63 -> 0.90
     * a run, runs with none 40 -> 26, comebacks 24 -> 38, mean unspent supply 1383 -> 590. The
     * gain is the quicker opening (both first units in 3 s), not the rally after a wipe.
     */
    levy: { stepPercent: 50, curvePercent: 25, rallyPercent: 50, rallyStepPercent: 50 },
    /**
     * Tactics (A27): every blow after the first on one target in a combat step hits 2 harder.
     * The garrison bonus is built and shipped at 0: at 1 the mirror was decisive on 22/40.
     * Digging in (A43, docs/INCREMENT-5.11-AMENDMENTS.md): 36 ticks = three move intervals by a
     * defend banner, 1 off every blow. With the script defending (scriptDefendSight 2) bonus 0,
     * 1, 2 left the fast preset decisive on 27, 28, 28 of 40: the bonus does not stall it.
     * Break contact (ADR-0013 slice 1, Increment 6.1): a unit ordered to a hex no enemy can
     * strike steps out at the next combat step and takes `partingBlows` (1) blows at full
     * attack from the strongest enemy in range. 1 est.: 0 makes leaving free, 2 a death
     * sentence for an archer (docs/RESEARCH-COMBAT-HOOKS.md, open question 1).
     * Veterans (ADR-0013 slice 2, Increment 6.2, A49 in docs/INCREMENT-6.2-REPORT.md): the
     * blow that completes a kill credits its attacker; at `veteranKills` (2) the unit wears
     * the gold plume and heals `veteranHeal` (1) more. `veteranAttack` is built and shipped
     * at 0: at the research's +1 the fight median fell 24 -> 20 ticks and escape 55 -> 50 %
     * (both on the slice's gate line) and the lone King rose 30 -> 32/40 over his guard;
     * heal-only keeps 24 ticks, 54 %, lone King 28, veterans 2.2 / 2.7 a run a side. At
     * `veteranKills` 3 a side made none in 33 of 40 runs; at 1 escape fell to 46 %. 99 turns
     * veterans off.
     * Breaking (ADR-0013 slice 3, Increment 6.3, A55-A56 in docs/INCREMENT-6.3-REPORT.md): a unit
     * at or under `breakShare` (34 %) of its HP with an enemy on it and no courage (an own unit
     * beside it, its King's reach, its squad, its own command, an own city under it) drops its
     * order, steps one hex toward the nearest own city at once and walks the rest, and takes no
     * order for `breakTicks` (24, two marches) or until it stands on an own city. A55: as the
     * research wrote it (a parting blow, the wait for the march) 82 of 110 broken units died
     * within 24 ticks; a free exit and the step at once bring that to 33 of 104. A56: at the
     * research's 25 % the fight median (20) and escape (50 %) sat on the slice's gate lines and
     * 5 of 40 mirror runs went to the clock; at a third (34) 30 ticks, 63 %, 0 to the clock,
     * decisive 40/40, lone King 20 (guard 30); at 50 the same with fewer comebacks (8). 0
     * turns it off.
     */
    tactics: {
      flankBonus: 2,
      garrisonBonus: 0,
      entrenchTicks: 36,
      entrenchBonus: 1,
      partingBlows: 1,
      veteranKills: 2,
      veteranAttack: 0,
      veteranHeal: 1,
      breakShare: 34,
      breakTicks: 24,
      /**
       * Heavy and glancing blows (FL2, docs/design/FOG-AND-LUCK-IDEA.md, section 6), on since the
       * owner's word of 2026-10-18 (N44, D-090); `?luck=off` plays without. One blow in oneIn lands
       * sizePercent of itself (rounded up) harder, one in oneIn as much softer, never under 1.
       * Chosen on the sketch's arithmetic (20,000 duels a cell: equal spears to the death 33 / 33 /
       * 34 %, a 6 HP lead still wins 95 %) and measured on the core's fight
       * (docs/INCREMENT-FL2-REPORT.md): lone spears 36 / 36 / 28, spears to the death 38 / 40 / 22
       * (under the gate's 25 for "together"; the owner accepted it), the fight median 28 against 28
       * on 200 seeds. 1 in 10 was the only knob over 25 and made a sword duel end together 62 %.
       */
      luck: { oneIn: 6, sizePercent: 50 },
    },
    /** Upkeep room: each working city supports this many units, built or in progress. */
    unitsPerCity: 3,
    /**
     * The roster (docs/design/UNITS-PIVOT-IDEA.md). `move` is hexes per move step, `range`
     * in hexes, `buildTicks` at 4 ticks/s, `wallAttack` per combat interval against walls,
     * `holdBonus` is defence while the unit has not moved for a move interval,
     * `capturesOnArrival` flips a wall-free foreign hex the moment the unit enters it.
     * HP is the Increment 4 roster times 1.5 (ADR-0013 slice 1, 2026-09-25): at 12/10/6/8/6 a
     * fight was three blows in 12 ticks and 39 % of units ever left one alive; at 18/15/9/12/9
     * the research measured a 24-tick median, escape 50 %, mirror decisive 36/40, lone King
     * 29/40 (docs/RESEARCH-COMBAT-HOOKS.md; the Increment 6.1 report re-measures the slice).
     * Build times are the Increment 4 roster times 2 (A50, docs/INCREMENT-7.1-AMENDMENTS.md,
     * from docs/design/ECONOMY-CAPTURE-PACE-ANALYSIS-2026-09-26.md): at 24/32/32/40/56 the
     * army was wiped 4.65 times a run and rebuilt in a median of 16 ticks; at x2 the mirror
     * ended 40/40 runs before the clock (36 at x1), a wiped side won 2 (8) and the pool held
     * 691 (837); x3 shortened the median run to 848 ticks and made no veterans in 25 runs.
     */
    units: {
      spear: {
        hp: 18,
        attack: 3,
        range: 1,
        move: 1,
        cost: 6,
        buildTicks: 48,
        wallAttack: 1,
        holdBonus: 2,
        capturesOnArrival: false,
      },
      sword: {
        hp: 15,
        attack: 5,
        range: 1,
        move: 1,
        cost: 8,
        buildTicks: 64,
        wallAttack: 1,
        holdBonus: 0,
        capturesOnArrival: false,
      },
      archer: {
        hp: 9,
        attack: 3,
        range: 2,
        move: 1,
        cost: 8,
        buildTicks: 64,
        wallAttack: 1,
        holdBonus: 0,
        capturesOnArrival: false,
      },
      rider: {
        hp: 12,
        attack: 3,
        range: 1,
        move: 2,
        cost: 10,
        buildTicks: 80,
        wallAttack: 1,
        holdBonus: 0,
        capturesOnArrival: true,
      },
      siege: {
        hp: 9,
        attack: 1,
        range: 1,
        move: 1,
        cost: 12,
        buildTicks: 112,
        wallAttack: 6,
        holdBonus: 0,
        capturesOnArrival: false,
      },
    },
    /** Enemy re-plans every 1.5 s at 4 ticks/s. */
    aiCadenceTicks: 6,
    /** The script counter-picks against opposing units within this many hexes of its land. */
    aiSightRange: 3,
    /**
     * Land (Increment 3.10, symmetric since 3.18). Rough hexes carry walls that block entry
     * until ground down (siege does it fast); they regrow one per production interval while
     * no hostile unit stands next to the hex. A neutral city holds walls too: taking it is a
     * short siege, not a walk. All est.
     */
    roughHexCount: 8,
    roughWalls: 4,
    structureCount: 3,
    structureProductionBonus: 1,
    neutralCityWalls: 4,
    /**
     * The land of cities (A51, docs/INCREMENT-7.1-AMENDMENTS.md, from the 2026-09-26 analysis
     * docs/design/ECONOMY-CAPTURE-PACE-ANALYSIS-2026-09-26.md): a faction's land within
     * cityReach of one of its working cities cannot be taken by standing, it changes hands
     * with the city, and a working city holds cityWalls (regrowing like a tower's) so taking
     * one is a siege. Measured before the rule: 57 % of the land taken from a faction lay
     * inside a working city's reach and 3.75 cities fell a run, each by a 2 s stand. The
     * numbers after it are in the Increment 7.1 report. cityHoldsLand false and cityWalls 0
     * give the ADR-0010 rule back.
     */
    cityWalls: 6,
    cityHoldsLand: true,
    /**
     * Cities (Increment 3.16, docs/INCREMENT-3.16-SPEC.md): both starts are cities and only
     * land within cityReach of an own working city pays supply; a working city pays
     * 1 + cityProductionBonus (A19: the city is the economy). Founding costs cityCost supply,
     * takes cityBuildTicks, needs cityMinSpacing from every other city. Spacing 3 since the
     * Increment 4 measurement: at 2 the script founded a city every two hexes, held 36 units
     * on 40 hexes and jammed (docs/INCREMENT-4-REPORT.md).
     */
    cityReach: 2,
    cityProductionBonus: 3,
    cityCost: 20,
    cityBuildTicks: 80,
    cityMinSpacing: 3,
    /** Random starts (Increment 3.11, A16): twin edge hexes at least this far apart. */
    minStartDistance: 6,
    startRejectionLimit: 20,
    /**
     * Tower (Increment 3.12): buildCost supply on an own plain hex, walls after buildTicks,
     * regrowing to towerWalls. The enemy raises them on its border on its build cadence.
     */
    buildCost: 10,
    buildTicks: 40,
    towerWalls: 10,
    aiBuildCadenceTicks: 48,
    /**
     * Enemy surrender (Increment 3.15): at most surrenderHexShare of the player's hexes and
     * at most surrenderStrengthShare of the player's total unit HP, for surrenderTicks in a
     * row. An idle player fields no units, so the enemy never yields to one. The hex share is
     * 0.4 since A31 (docs/INCREMENT-5.4-AMENDMENTS.md): at 0.25 the player's won runs ended at
     * a median tick of 1555 and 8 of 22 dragged to the clock; at 0.4, 1275 and 4.
     */
    surrenderHexShare: 0.4,
    surrenderStrengthShare: 0.5,
    surrenderTicks: 120,
    /**
     * Sudden death (A57, docs/INCREMENT-7.4-REPORT.md): from 6:00 (1440 ticks at 4 a second)
     * the first hex to change hands ends the run for the side holding more land; a tie plays
     * on. The clock turns red. 0 turns it off. Over 80 mirror seeds the scripts end at a
     * median of 920 ticks, so 6:00 fired in 9 runs and moved nothing (dead time 0.44); 4:00
     * (960) gave 0.41 for a fifth fewer veterans, 3:00 (720) 0.36 by cutting comebacks 23 -> 17.
     * The owner's default, kept for the human's long runs; the phone judges it.
     */
    suddenDeathTick: 1440,
    /**
     * The court (ADR-0011, docs/design/KING-DELEGATION-IDEA.md): a King per faction, every
     * command needs its reach and, stop aside, spends Will. Reach 1 (7 hexes) with one working
     * city, 2 (19 hexes) from the second. Trust, the pool size, is 3 + 2 for the capital + 1 per
     * other working city, cap 8, less 2 per flight, floor 1: 5 at the start. One Will per
     * production interval (2 s) on the capital, every second one (4 s) on another own city,
     * every third (6 s) in the field: about 30, 15 and 10 commands a minute. All est.; the
     * gates they were measured against are in docs/INCREMENT-5.1-REPORT.md.
     */
    court: {
      kingReach: 1,
      kingReachGrown: 2,
      kingReachCities: 2,
      trustBase: 3,
      trustCapital: 2,
      trustPerCity: 1,
      trustMax: 8,
      flightPenalty: 2,
      refillIntervals: { capital: 1, city: 2, field: 3 },
      costs: {
        order: 1,
        train: 1,
        tower: 1,
        city: 2,
        improve: 1,
        raze: 2,
        appoint: 1,
        promote: 2,
        aim: 1,
        stance: 1,
        call: 2,
        mark: 2,
        tax: 2,
        rush: 2,
      },
      /** The script's King values a city it can recruit in, or a city it can found, as two idle units. */
      scriptCityWeight: 2,
      /**
       * A32 (docs/INCREMENT-5.6-AMENDMENTS.md): a mayor is a soldier sent to the city, so the
       * office costs the soldier and costs.appoint drops 2 -> 1 (at 2 the mirror was decisive
       * on 23/40). The script spares a soldier only from an army of six: swept 1, 3, 4, 5, 6 on
       * the pace seeds, mirror decisive 24, 23, 27, 25, 29 of 40, lone King wins 29, 27, 27,
       * 27, 25 of 40.
       */
      scriptGovernorArmy: 6,
      /**
       * Mayors (slice 2): each holds a pip of Trust. A mayor acts every 6 s and trains only
       * while the pool keeps more than the reserve after the price. Both est.; measured in
       * docs/INCREMENT-5.2-REPORT.md.
       */
      mayorCadenceTicks: 24,
      mayorReserve: 10,
      /**
       * Commanders (slice 3): promoted for costs.promote Will, each holds a pip of Trust while
       * alive, leads at most squadSize followers gathered by contact, decides every 3 s (one
       * move interval) and sees commanderSight hexes. The script keeps one per
       * scriptCitiesPerCommander working cities, rounded down. Cadence 12, not the design's 24:
       * at 24 the mirror was decisive on 23/40 seeds and the delegating King won 22/40; at 12,
       * 32/40 and 25/40 (docs/INCREMENT-5.3-REPORT.md). Sight 4 and one per 3 cities were worse.
       */
      squadSize: 3,
      commanderCadenceTicks: 12,
      commanderSight: 3,
      scriptCitiesPerCommander: 2,
      /**
       * The King as a general (A33-A34, docs/design/KING-GENERAL-IDEA.md), from the throne
       * only. The call: idle soldiers answer every 6 s (a mayor's cadence) into a ring of 1
       * around the mark. Tax: double supply income for 30 s, ready 2 min after it was raised.
       * Rush: one city trains in half the time for 30 s, same cooldown. All est.; the gates
       * they were measured against are in docs/INCREMENT-5.7-AMENDMENTS.md.
       * Banners (A39-A41, docs/INCREMENT-5.10-AMENDMENTS.md): three, planted within 8 hexes of
       * the King from anywhere. Range swept 4, 6, 8, 99 on the pace seeds: mirror decisive 33,
       * 33, 34, 34 of 40, nothing else moved, so the owner's 8 ships on every map.
       * A42: a commander alone at a muster point strikes after 12 s (est.). scriptDefendSight
       * ships at 0: at 2 the fast preset's decisive runs fell 31 -> 27 of 40 (the banner pulls
       * the script's soldiers home), docs/INCREMENT-5.11-AMENDMENTS.md.
       */
      /**
       * The lords (A45-A47, docs/INCREMENT-5.12-AMENDMENTS.md): a commission costs a city's
       * price; three soldiers by the city because a city supports three. scriptMusters ships
       * off: on, the mirror was decisive on 32/40 (34 off) and the median run ended at tick
       * 1243 (1160 off).
       */
      lords: { commission: 20, musterSize: 3, scriptMusters: false },
      general: {
        callCadenceTicks: 24,
        callRadius: 1,
        markRange: 8,
        markSlots: 3,
        musterTicks: 48,
        taxPercent: 200,
        taxTicks: 120,
        taxCooldownTicks: 480,
        rushPercent: 50,
        rushTicks: 120,
        rushCooldownTicks: 480,
        scriptCalls: true,
        scriptDefendSight: 0,
      },
    },
    /**
     * Population (A53, docs/INCREMENT-7.2-AMENDMENTS.md; E3 of the 2026-09-26 analysis): the
     * land grows recruits, a soldier costs one, a dead soldier's is gone, a governor's comes
     * back. Every production interval each hex in reach adds 1 growth and each working city
     * cityGrowth; perRecruit growth is one recruit, never past max. With one city on its own
     * hex that is a recruit every 30 s, with 19 hexes in reach every 10 s; a side under
     * rallyHexPercent of its rival's land grows at rallyPercent. Measured (7.2 report): at
     * perRecruit 100 the rule never binds (starved 4 % of the time, the run is 7.1's); at 150
     * a side sits with room and no recruit 11 % of the time, a wiped side wins 6 runs of 40
     * (1 in 7.1), comebacks 10 (14 in 7.1, the analysis's gate was >= 12: the owner rules,
     * N12); at 200-250 starved 31-51 % and comebacks 8-10. The rally moved comebacks 9 -> 10-11
     * at every setting swept. null gives the ADR-0010 rule back; unitCost 0 keeps the number
     * and drops the price.
     */
    population: {
      perRecruit: 150,
      cityGrowth: 10,
      start: 2,
      unitCost: 1,
      max: 6,
      rallyPercent: 200,
      rallyHexPercent: 70,
    },
    /**
     * Improvements on the land (A54, docs/INCREMENT-7.3-AMENDMENTS.md; E4 of the 2026-09-26
     * analysis): supply buys a farm or a hamlet on an own plain hex a working city reaches. A
     * farm pays farmSupply more an interval (cost 15 back in 30 s), a hamlet grows hamletGrowth
     * more (at perRecruit 150 a recruit a minute on its own). The King places one for
     * costs.improve Will; a mayor told to develop buys one a cadence, a farm and a hamlet in
     * turn, for none. The script builds them by its King from scriptDevelopSupply in the pool
     * on its build cadence and tells a guarding mayor to develop from the same floor (0:
     * never). Measured (7.3 report, 40 mirror seeds): the mayor alone built 4.8 a run (the
     * script has a mayor 11 % of the time); with the King 10.6 at floor 100 and the pool rose
     * 710 -> 857 (a farm is a source, the sink is 150 a run), mirror decisive 37 -> 32; at 200,
     * 8.2 a run, pool 678, decisive 38; at 400, 7.8 and 749. Price 10-25, farm 1-2 and hamlet
     * 2-10 moved nothing but the pool (farm 2: 950). The analysis gate (pool under 400) is
     * not met by this rule; the owner rules (N14).
     */
    improvements: {
      cost: 15,
      buildTicks: 40,
      farmSupply: 1,
      hamletGrowth: 5,
      scriptDevelopSupply: 200,
    },
    /** The duel is played as the King; `?seat=soldier` swaps in `soldierSeat` (ADR-0014 M1). */
    soldier: null,
    /** The King's duel has no finds; the soldier's seat swaps in `finds` below (ADR-0014 M2). */
    finds: null,
    /** The King's duel pays nobody; the soldier's seat swaps in `bounty` below (ADR-0015 C1). */
    bounty: null,
    /** The King's duel has no company rules; the soldier's seat swaps in `company` below (ADR-0015 C2). */
    company: null,
  },
  /**
   * The soldier's seat (ADR-0014 slice M1, docs/design/MULTIPLAYER-ROLES-IDEA.md): the human is
   * one Spear of the player's kingdom, the scripts play its King, mayors and commanders, and
   * the human's unit takes no order but its own (D8 default). Dead, it returns as a Spear at
   * the capital after respawnTicks (12 s, the sketch's default). Both est.: the first slice
   * measured at the desk, not by the sim.
   */
  soldierSeat: { kind: "spear", respawnTicks: 48 } as const,
  /**
   * The company duel (K1, `?mode=company`, docs/design/KINGDOM-SEATS-IDEA.md sections 6 and 7.2):
   * a King and four soldier seats in each kingdom, the duel's rules under City Command plus the
   * soldier's blocks (finds, the King's pay, company), every seat but the human's scripted. A dead
   * seat rises after `soldierSeat.respawnTicks` at its kingdom's wave (every 5 s), by the own city
   * it chose, else the nearest one with no rival within 2 (FL1's alarm sight), on the rings round
   * it. The map is bigger than the duel's for eight units more (80-100 hexes; one unit a hex).
   * Measured (`test/sim/companySeats.sim.ts`, 200 seeds, every seat scripted): ringRadius 3, not
   * the sketch's 2: at 2 a capital on the map's edge had room for fewer than four, 25 jams before
   * tick 240 (57 in all, 8 runs), at 3 none (32 in 5 runs, each with its kingdom down to one city);
   * the parity mirror 108/200 either way; peak units on 80-100 hexes 0.28 of the map (worst 0.37),
   * the duel's own on 40-60 hexes 0.26 (worst 0.42). perKingdom, waveTicks and safeRange est.
   */
  companyMode: {
    seats: { perKingdom: 4, waveTicks: 20, safeRange: 2, ringRadius: 3 },
    mapHexCount: { min: 80, max: 100 },
  },
  /**
   * Two against two (K6, `?mode=allies`, docs/design/KINGDOM-SEATS-IDEA.md section 9): the duel's
   * rules for the seat the human holds, four kingdoms on two sides, the player beside seat3
   * against the enemy beside seat4, the starts in C2's orbits (K5) so the enemy is the player's
   * twin and seat4 seat3's. The map is the duel's per kingdom (40-60 hexes for two), est.
   */
  alliesMode: {
    sides: [
      ["player", "seat3"],
      ["enemy", "seat4"],
    ],
    symmetry: "C2",
    mapHexCount: { min: 80, max: 120 },
  },
  /**
   * The war (K7, `?mode=war`, docs/design/KINGDOM-SEATS-IDEA.md section 9): two sides of ten
   * kingdoms, the duel's rules for the seat the human holds plus the fall of a capital (ADR-0012
   * W4), the starts in C2's orbits so the enemy is the player's twin and seat12 seat3's. The map is
   * the sketch's about 720 hexes (36 a kingdom), its rough ground and structures the duel's for
   * every two kingdoms; the bell at the sketch's 20 minutes and sudden death at three quarters of
   * it, as the duel's 6:00 of 8:00. All est.
   */
  warMode: {
    sides: [
      ["player", "seat3", "seat4", "seat5", "seat6", "seat7", "seat8", "seat9", "seat10", "seat11"],
      [
        "enemy",
        "seat12",
        "seat13",
        "seat14",
        "seat15",
        "seat16",
        "seat17",
        "seat18",
        "seat19",
        "seat20",
      ],
    ],
    symmetry: "C2",
    mapHexCount: { min: 660, max: 780 },
    land: { roughHexCount: 80, structureCount: 30 },
    runLengthSec: 20 * 60,
    suddenDeathSec: 15 * 60,
  },
  /**
   * Symmetric starts (K5, `rules.starts`, docs/design/KINGDOM-SEATS-IDEA.md section 7.1), for a
   * mode of more than two kingdoms; the first to play them is K6's two against two (`alliesMode`). Inside a side, the largest
   * nearest-land share at most spreadPercent percent of the smallest (the sketch's 1.25x, est.),
   * else the side is drawn again, draws times at most, the least spread kept. Measured on the
   * starts alone (200 seeds; a free-for-all's max/min a seed, every kingdom): 16 kingdoms in D2 on
   * 601 hexes 1.74 at 20 draws (`startRejectionLimit`), 1.54 at 50, 1.43 at 100, 1.34 at 200;
   * 10 in C2 on 301 hexes 1.66, 1.51, 1.42, 1.34; 8 in D2 on 241 hexes 1.12 from 50 on. 100
   * meets the sketch's 1.5 for 16; a 16-kingdom createRun took 47 ms with it on this host while
   * other sessions' probes ran (200 in 9.3 s, test/symmetricStarts.test.ts).
   */
  kingdomStarts: { spreadPercent: 125, draws: 100 },
  /**
   * The Soldier works its road (CC5, A72, contract S7), built and shipped OFF: the contract turns
   * it on once its gates hold, and the friction gate (orders per gem at most 0.5x) missed. To turn
   * it on, spread this block into `soldierSeat`. freeOrders lifts the order lock from the human's
   * unit and ends an order onto an own unit beside it; workRoute makes a walk read and take the
   * land on its road and then the ring of patchRadius 1 around its end, never onto a hex within
   * enemyRange 2 of an enemy, until an enemy comes that near or a find is taken. Measured
   * (`test/sim/work.sim.ts`, 400 seeds, against the seat as shipped): the roamer's purse 106.2 ->
   * 124.1, the companion 0.89x of it, the standard-bearer 0.96x, the chaser 1.00x (A67 holds); the
   * King's pay 32.1 % of the roamer's purse; orders per gem 0.79x to 0.85x (band 0.5x: a miss);
   * deaths 1.04x to 1.09x, the companion's 1.15x (band 1.10x: a miss; 1.11x without the patch's
   * enemy filter). The land alone (`test/sim/finds.sim.ts`, 200 seeds, 4 minutes): purse 69.3 ->
   * 64.6, spread 2.27x -> 2.30x; in the war 48.2 -> 51.2 (far market) and 48.5 -> 54.4 (near).
   */
  soldierWork: {
    freeOrders: true,
    workRoute: { patchRadius: 1, enemyRange: 2 },
  } as const,
  /**
   * Valor and its six titles (V1 of docs/design/SOLDIER-GROWTH-IDEA.md): the seat's strength for
   * this run, earned by deeds at work (A62), in the duel's soldier seat once its gates G1-G11 hold
   * (`soldierDuelValor` below). Section 5.1's deeds; a squad's hex at 0.75 of the same hex alone (1.5)
   * and `step` 3 (titles at 0, 6, 15, 27, 42, 60), both measured by V0
   * (`test/sim/strength.sim.ts`, 400 seeds: the weight leaves the roads closest together, G3's
   * worst at 0.79x of the best at every weight; 13 of 15 checks of G1, G2 and the cap's brake
   * pass at 2.75 and at 3, a tie to the larger step). A death takes a quarter (decision 4). The
   * grants by title, Levy first (section 2): Sergeant and Captain 2 HP more and the Champion 4 (any
   * HP above a Spear's 18 wins a meeting, section 5.3); hold 1 more dug in from Shieldman; no parting
   * blow from Captain; the Champion's attack 0 until G4 and G6 pass with it at 1 (decision 3,
   * A49's lesson). A title reached the first time in a run heals 4 (the second wind). Measured
   * by V1 (`test/sim/valorTune.sim.ts`, 200 seeds, four working habits): the Spearman's faster
   * heal of the sketch (1 more a production interval) made the soldier die 0.60-0.72x as often as
   * with growth off (G7 asks at least 0.75x) and the fighter's kingdom win +17 net (G4's band is
   * 14 at 400 seeds); without it 0.78-0.83x and +8, so `heal` is 0 at every title. V1 put the
   * King's banner at 3, not 4 (the sketch's own lever, section 5.1): at 4 the chaser and the
   * standard-bearer were crowned in 30 % and 27 % of runs with 10 % of their valor past the
   * Champion (the cap's brake asks under 10 %), at 3 in 25 % and 24 % with 9 %. Measured again
   * under FL2's luck (D-090; `test/sim/strength.sim.ts` with `SIM_GROWTH=valor`, 400 seeds from 100
   * and from 500, D-129): with the sketch's HP (3, 3, 6) and the banner at 3, the chaser's kingdom won
   * +21 of 400 (z 2.73, G4's band 14), the fighter's peak was 0.80x of the best (G3) and the chaser
   * earned 11 % past the Champion; at HP 2, 2, 4 and the banner at 2 (the squad's 1.5, back at the
   * squad weight 0.75 from 0.83) every working habit's net is -6 to +13, the worst road 0.84x,
   * past the Champion at most 8 %, deaths 0.83-0.95x of growth off on the working roads and 0.78x
   * standing aside (G7 asks at least 0.75x). Luck lets each HP point turn a share of every close
   * fight, which no HP did without it. Every other number is the sketch's est.
   */
  soldierValor: {
    step: 3,
    deeds: {
      land: 2,
      banner: 2,
      steadied: 4,
      slay: 6,
      veteranSlay: 12,
      together: 3,
      broke: 2,
      squad: 1.5,
      squadBanner: 1.5,
    },
    deathPercent: 25,
    hp: [0, 0, 0, 2, 2, 4],
    heal: [0, 0, 0, 0, 0, 0],
    hold: [0, 0, 1, 1, 1, 1],
    attack: [0, 0, 0, 0, 0, 0],
    freeExit: [false, false, false, false, true, true],
    secondWind: 4,
  } as const,
  /**
   * Valor in the duel's soldier seat (`?seat=soldier`, D-129), shipped off: with the block above
   * every gate of V1 passes under luck on 400 seeds twice but G6 on seeds 100-499, where the
   * fighter's map median fight reads 28 ticks against 32 (a combat interval's step; its mean fight
   * 95.7 against 96.1 there, 94.2 against 96.2 on 500-899, where the median holds). The owner
   * kept G6 as written and the switch off until the desk (N53, D-130); true ships the seat with
   * valor, never with Command and head money (G4, D-088). `?valor=on` plays all three for the desk,
   * `?valor=off` none. The company's soldier seats (K1) keep valor off: unmeasured with eight.
   */
  soldierDuelValor: false,
  /**
   * The run's coins (VC of docs/design/SOLDIER-GROWTH-IDEA.md, section 4; D-087): played with
   * `soldierValor` (`?valor=on` spreads both into the soldier's seat). At or beside an own working
   * city, at work, empty-handed and from Spearman, `commandPrice` coins make the soldier a leader
   * for this life: idle soldiers beside it join by contact, `men` by title (Levy first). A kill by
   * its blow at work is paid `killBounty` from the King's pool, an enemy veteran's
   * `veteranBounty`, owed up to the pay's debt cap. Every number is the sketch's est.: the price is
   * a scripted lord's commission (`lords.commission`), the head money the land bounty's price.
   */
  soldierCoins: {
    commandPrice: 20,
    men: [0, 1, 1, 2, 2, 3],
    killBounty: 3,
    veteranBounty: 6,
  } as const,
  /**
   * The purse and the finds (ADR-0014 slice M2, docs/INCREMENT-M2-REPORT.md): a hidden layer
   * under the land, a pure function of the seed. The best 15 % of the map by seeded noise holds
   * a find (the sketch's share), rough ground favoured; the richest 15 % of the finds are worth
   * 3, the next down to the median 2, the rest 1. A soldier standing idle for one production
   * interval (2 s) with no enemy beside it learns its hex, a veteran the six around it; empty
   * hands take the find. Under a load it steps every second move interval (6 s a hex). A working
   * city of its kingdom pays worth x basePrice, 100 % more per cityReach hexes the find lay from
   * it, at most three times, and keeps a fifth into the kingdom's pool. Every number is the
   * sketch's default or est. Measured (M2 report, 40 seeds, a scripted prospector): roughPercent
   * 115 makes rough ground hold a find twice as often as open ground (26 % against 13 %; at 150
   * it was 54 % against 7 %). With the land to itself for 4 minutes the purse ends at a mean
   * of 52.6 and the best quarter of the seeds earns 2.43 times the worst (the sketch's gate is
   * under 2: not met at 15 %; 2.28 at 20 %, 1.99 at 25 %; the owner rules, N16). In the war a
   * run pays a mean of 36 and the soldier dies 2.5 to 3 times: the war, not the land, decides.
   * A67 (docs/INCREMENT-C2.2-REPORT.md): the share is 25 %. With the land to itself the spread
   * is 1.97x (the gate, under 2, is met) and the first sale comes at 24 s, not 45 s; in the war
   * (200 seeds) a roamer sells 5.09 loads a run, not 3.41.
   */
  finds: {
    sharePercent: 25,
    veinScale: 2.5,
    roughPercent: 115,
    richPercent: 15,
    fairPercent: 50,
    prospectTicks: 8,
    basePrice: 4,
    maxPricePercent: 300,
    marketCutPercent: 20,
    carryMoveIntervals: 2,
  },
  /**
   * The kingdom pays (ADR-0015 slice C1, docs/INCREMENT-C1-REPORT.md): the human's soldier is
   * paid from its kingdom's pool for a hex it took by standing and for its King's attack banner
   * met while it stood in the banner's ring; what the pool cannot pay is owed up to debtCap and
   * paid at the production step. Each deed earns renown. Every number is est. (the owner's
   * defaults of N19). Measured with them (C1 report, 40 seeds, the scripted prospector): the
   * King's pay is 21.6 of a purse of 52.5 a run (41 %; the gate is 30 %), renown 10.8 a run,
   * the kingdom wins 30/40 with and without it; with finds on 25 % of the land the pay is 26 %
   * of the purse. No banner was paid and no debt arose in 40 scripted runs.
   * A67 (docs/INCREMENT-C2.2-REPORT.md): landBounty 3 and bannerBounty 9 with finds on 25 % of
   * the land. At 2 and 6 the King's pay was 24 % of the roamer's purse (200 seeds; the gate is
   * 30 %); at 3 and 9 it is 32 %, and the soldier that walks to its King's banner ends with
   * 1.06 times the purse of the one that ignores him.
   */
  bounty: { landBounty: 3, bannerBounty: 9, landRenown: 1, bannerRenown: 2, debtCap: 40 },
  /**
   * Company (ADR-0015 slice C2, docs/INCREMENT-C2-REPORT.md): with a comrade beside it (another
   * unbroken unit of its kingdom on a neighbouring hex) a carrier steps at the full pace, a load
   * sells for salePercent of the city's price and a hex is read in prospectTicks (1 s against
   * the 2 s of finds.prospectTicks); a broken unit of either kingdom is steadied at the combat
   * step; the soldier that fell in with a commander shares the land bounty of his squad within
   * squadRing of him. Renown for a load sold in company, a broken comrade the soldier came to
   * and a kill struck together. Every number is the research's default, est. Measured with
   * them (C2 report, 40 seeds): the companion ends a run with 50.2 against 66.9 for the
   * prospector that works anywhere (0.75x; the research's gate is 1.25x, A64), dies 1.32 times
   * against 2.50 and meets 61 % of its danger beside an own soldier; 48 % of the roamer's sales
   * are made in company; salePercent 100 and 200 give 0.76x and 0.74x, squadRing 4 gives 0.86x.
   * scriptBanners (A66, docs/INCREMENT-C2.1-REPORT.md): the scripted King keeps an attack banner
   * flying by his squads and waits for its price before his other commands. Measured (200
   * seeds): 4.65 banners planted a run and 3.44 met (0.10 and 0.01 without it), the companion
   * 0.80x of the roamer's purse and the one that stands by the banner 0.84x (0.76x without it),
   * the kingdom of a working soldier wins 148 to 154 of 200 (150 to 152), the median run lasts
   * 1027 to 1171 ticks (967 to 980). The gate 1.25x is not met; the owner rules (N24).
   * squadBanner (A68, docs/INCREMENT-C2.2-REPORT.md): a banner the squad stands by when it is
   * met pays the soldier that marches with the squad, out of the banner's ring too. Measured
   * with A67's numbers (200 seeds): the companion 0.91x of the roamer's purse (0.84x without
   * it), the one that stands by the banner 0.98x, the roamer that walks to the banner 1.06x;
   * the companion dies 1.74 times a run against the roamer's 2.43. squadRing stays 2: at 3 the
   * companion earns 0.97x and dies 2.17 times, at 4 it meets less than half of its danger
   * beside an own soldier. A67 retires the gate 1.25x for "no road is dominated".
   */
  company: {
    salePercent: 150,
    prospectTicks: 4,
    squadRing: 2,
    saleRenown: 1,
    steadyRenown: 2,
    killRenown: 1,
    scriptBanners: true,
    squadBanner: true,
  },
  /**
   * The King's seat plays classic (the court) until City Command earns the flip (D-075: CC1's
   * bands with the rival on the same rules, CC4, and CC2's e2e); `seatRulesOf(seat, "cities")`
   * asks for City Command.
   */
  controls: { scheme: "cities" as ControlScheme },
  /**
   * City Command (Increment CC1, the controls plan of 2026-10-17, contract S2), the King's seat
   * with `"cities"` controls only. Since CC4 (contract S5, one rulebook) it binds every kingdom
   * of such a run (`scope: "all"`): the scripted rival plays City Command too, one command each
   * 24 ticks (6 s, the plan's S5 rate; 10 a minute at most), and founds only with 20 over a
   * city's price in the pool (the advice proxy's margin). The rest is CC1's: the duty every 12
   * ticks (3 s, one march) on the tick before the march; the King walks 1 hex a march; his city
   * trains in half the time while he stands in it (the rush's 50 %); a flight shakes him for 120
   * ticks (30 s, the rush's length) in place of the Will and Trust it cost; an arrow's ring is 1
   * (the banners' callRadius); before walls a column gathers 2 or waits 48 ticks (12 s, A42's
   * muster); Home sends 2 beside an intruder; a full city develops while the pool keeps 60 after
   * the price; frontier founding because an own site 3 from every city almost never exists in the
   * first minute (the plan, §1). Measured against the city script on 200 seeds once soldiers give
   * way (CC4): the idle King lost 200/200 before the clock, the advice won 140/200 (band 70-150),
   * its first free city at a median tick of 116; the first CC4 build's duty 24 and muster 24
   * passed too (advice 134/200, first free city at 140, one idle run to the clock) but slowed the
   * human's columns for nothing (A71). All est. but the King's pace (A70): the plan's mounted King (2 hexes a
   * march) let a King who never sets an arrow and only founds win 9/80 and 24/200 against the
   * court script, 20 of the 24 on hexes at the clock (band: at most 4/40); at 1 hex, 4/80 and
   * 14/200, and no other probe moved (advice 79/80, first free city at a median tick of 116).
   * Sweep rows and the misses are in the CC1 and CC4 reports (`test/sim/cities.sim.ts`).
   */
  cityCommand: {
    scope: "all",
    dutyCadenceTicks: 12,
    kingStepHexes: 1,
    presencePercent: 50,
    shakenTicks: 120,
    rallyRadius: 1,
    musterSize: 2,
    musterTicks: 48,
    homeGuardPerIntruder: 2,
    developFloor: 60,
    frontierFounding: true,
    scriptCommandTicks: 24,
    foundMargin: 20,
  },
  /**
   * The soldier's screen (Increment 5.15, docs/INCREMENT-5.15-REPORT.md): the goal's ring on the
   * map breathes once in goalPulseMs between the two ratios of the hex radius; the dashed road
   * to it and the outline of the land where Sell works are drawn at guideAlpha (since D-206 the
   * kingdom's other soldiers' roads are not drawn on this screen); a city's price other than the best one
   * is drawn at priceRestAlpha, the best at bestPriceScale of the label size; what the seat
   * earned floats up from the soldier for gainMs, gainRiseRatio of the hex radius. All est.,
   * judged by eye on the owner's screenshot of 2026-09-27; nothing here is a rule.
   *
   * Increment 5.21 (the soldier reads less): the road under a resting pointer is drawn only
   * within hoverRoadHexes of the soldier (or while dragging); health bars stand on units
   * within hpSightHexes of it (since UP1 without a number: the number shows only in focus). Tags (the goal's on its hex, the time over a standing soldier,
   * the tooltip) are tagFontPx text on a dark panel, tagIconRatio of it per icon, the goal's
   * tag tagYRatio of the hex radius under its middle, the standing one over the soldier's
   * figure, the tooltip tooltipOffsetPx right of and over the pointer. The countdown
   * rings (broken, fallen) sit countdownRingRatio of the hex out; the queued order's ghost ring
   * is drawn at queuedAlpha with its clock clockHalfRatio of the hex. All est., judged by
   * frame captures at 1280 x 720.
   */
  soldierScreen: {
    goalPulseMs: 1400,
    goalRingMinRatio: 0.7,
    goalRingMaxRatio: 0.84,
    guideAlpha: 0.75,
    priceRestAlpha: 0.6,
    bestPriceScale: 1.25,
    gainMs: 1600,
    gainRiseRatio: 1.1,
    hoverRoadHexes: 3,
    hpSightHexes: 2,
    tagFontPx: 13,
    tagIconRatio: 0.45,
    tagPaddingPx: 4,
    tagGapPx: 3,
    tagAlpha: 0.82,
    tagYRatio: 0.92,
    tooltipOffsetPx: 14,
    countdownRingRatio: 0.92,
    countdownWidthPx: 3.5,
    queuedAlpha: 0.55,
    clockHalfRatio: 0.22,
    /**
     * FL2: under luck the fight's tag is a win or a loss when one side's chance to kill first
     * leads the other's by this many points, else even (equal Spears: 33 / 33, even). Est.
     */
    oddsMarginPercent: 20,
  },
  /**
   * The King's screen (Increment 5.16, docs/INCREMENT-5.16-REPORT.md): the court's advice calls
   * a soldier idle after idleSoldierTicks without a step or an order (6 s, the cadence the
   * call's own soldiers answer on) and a city threatened with an enemy within threatSight of
   * it and no soldier of the kingdom that near; a top band narrower than compactBandWidthPx
   * keeps the icons and the numbers of the King's counters and drops their words. All est.;
   * nothing here is a rule.
   */
  kingScreen: { idleSoldierTicks: 24, threatSight: 1, compactBandWidthPx: 480 },
  /**
   * The rival's plans hidden (FL1, docs/design/FOG-AND-LUCK-IDEA.md): no rival road or arrow is
   * drawn, and an own working city with a rival unit within alarmSight hexes and none of the
   * seat's as near breathes a red rim, the defend suggestion's (CC3). 2 is two marches' warning
   * (a step every 3 s); est., judged at the owner's desk.
   */
  rivalScreen: { alarmSight: 2 },
  /**
   * The field tips (HG1, docs/design/HELP-GUIDE-IDEA.md section 4.B): the first no sooner than
   * firstDelayMs into a run, one at a time, gapMs of quiet after one before the next (an urgent
   * one, a city in danger or the soldier fallen or broken, waits firstDelayMs only), each until its
   * deed or showMs, faded in and out over fadeMs; `send` and `walk` from startTicks, `ghost` from
   * ghostTicks, `fight` within fightSight hexes. A tip whose moment passes by itself stands readMs
   * first (G18: the cold start of 2026-10-25 lost "found" in 2 s and "defend" in 2-6 s); a 15-word
   * line read at about three words a second after a second to see it. `room` (G19) once the army
   * stands at its room with roomSupply in the pool: the cold start of 2026-10-25 watched supply
   * climb to 929 beside an army of 3 of 3 with nothing to say what a city is for. All est., tuned
   * at the owner's desk.
   */
  tips: {
    firstDelayMs: 2500,
    gapMs: 20000,
    readMs: 6000,
    roomSupply: 100,
    showMs: 14000,
    fadeMs: 350,
    startTicks: 4,
    ghostTicks: 120,
    fightSight: 2,
  },
  /**
   * The co-op screens (K3, docs/design/KINGDOM-SEATS-IDEA.md section 11): a comrade human's name
   * over its soldier (its bottom nameLift unit half-heights above the drawn centre) and over the
   * King a human holds (nameGapPx above his crown), at nameFontPx; the roster under the band, a
   * row rosterRowPx high at rosterFontPx, rosterInsetPx under the band; a fallen soldier's other
   * cities to rise by in a rim choiceRimPx wide at choiceRimAlpha.
   * All est., judged in frames at 1280 x 720 (`build/scratch/k3`; the rim at 2 px and 0.6 was lost
   * on an orange hex).
   */
  coopScreen: {
    nameFontPx: 12,
    nameLift: 1.2,
    nameGapPx: 4,
    rosterFontPx: 13,
    rosterRowPx: 18,
    rosterInsetPx: 10,
    choiceRimPx: 3,
    choiceRimAlpha: 0.85,
  },
  /**
   * The kingdom's ledger and the payday (Increment 5.19, docs/INCREMENT-5.19-REPORT.md; CV3.1,
   * docs/INCREMENT-CV3.1-REPORT.md). Every production step the counter rolls up to the pool over
   * rollMs and nothing else moves. Only a payday that is news is played (`read/paydayShow.ts`):
   * the run's first, a city's first, a pile grown a coin, or a tribute from every paying city
   * once the coins have rested tributeEveryMs. Played every step, 12 of the 12 paydays of 25 s
   * of a four-city kingdom flew coins across the map, and the owner found it spam (2026-10-20).
   * A played payday gleams its cities' piles (`cityLook.gleamMs`), raises "+N" over each
   * (`soldierScreen.gainMs`), then their coins leave the piles for the supply counter: one per
   * coinWorth of what the city paid, at most coinsPerCity a city and coinsPerPayday in all,
   * coinGapMs apart, flightMs in the air on an arc arcRatio of the way high (arcMaxPx at most:
   * at 0.22 of a 585 px flight the coins left the screen by the top, seen in the pane), turning
   * spinTurns times. Each coin carries its part of the sum: the counter rolls up as each lands,
   * a ring clinkScale times the coin's size spreads from the counter's coin over clinkMs, and
   * the coin's icon and the income swell to bumpScale for bumpMs. No payday is played within
   * restMs of the last one: its news waits for the next payday. 1500 let two play 2 s apart; at
   * 4000 the King's seat under the suggestion played 581 against 586 on 40 seeds (3.54 a minute
   * against 30 before CV3.1), the shortest gap 4.0 s (`test/sim/payday.sim.ts`). A city gained or
   * lost colours the score for cityPulseMs. The dashed rim of a city's land is drawn at
   * rimAlpha. All est. until the owner's desk; nothing here is a rule.
   */
  ledger: {
    tributeEveryMs: 30000,
    spinTurns: 1.5,
    clinkMs: 260,
    clinkScale: 2.4,
    coinWorth: 6,
    coinsPerCity: 3,
    coinsPerPayday: 9,
    coinGapMs: 80,
    flightMs: 640,
    arcRatio: 0.22,
    arcMaxPx: 48,
    coinHalfPx: 7,
    rollMs: 450,
    bumpMs: 280,
    bumpScale: 1.35,
    restMs: 4000,
    cityPulseMs: 1600,
    rimAlpha: 0.85,
  },
  /** Procgen map size bounds (hex count) for the MVP. */
  mapHexCount: { min: 40, max: 60 },
  /** Max hex distance from the origin; radius 5 holds 91 hexes, enough slack for irregular blobs. */
  mapMaxRadius: 5,
  /** Narrowest supported viewport in CSS px. */
  minViewportWidthPx: 380,
  /** Gap between the map's bounding box and the viewport edge. */
  mapPaddingPx: 16,
  /** Band reserved at the top of the viewport for the HUD so it never overlaps the map. */
  hudHeightPx: 44,
  /**
   * Smallest map label; two digits must read at 380 px wide, DPR 1. Since UP1 a health number
   * beside a King, or one that would leave its tile, may shrink to `unitPieces.digitsFloorPx`.
   */
  labelMinFontPx: 12,
  /**
   * Kingdom borders (Increment 5.14, docs/INCREMENT-5.14-REPORT.md): a line in a light tint
   * of the owner's colour along every edge of owned land that faces another owner, nobody's
   * land, razed land or the map's edge, so a kingdom reads as one shape and not as tiles.
   * Width against the hex radius with a floor in px (the world view under 11 px keeps the
   * floor); tint is the share of white mixed into the owner's fill; the line is inset by half
   * its width so both kingdoms' borders show on a shared edge. The seam between two tiles of
   * one owner is filled with the fill darkened by seamShade instead of the background, so a
   * kingdom is one body with a faint tiling, not tiles on black. All est.; the phone decides.
   */
  mapBorders: { widthRatio: 0.08, minWidthPx: 2, tint: 0.42, alpha: 0.92, seamShade: 0.72 },
  /**
   * Map shape (Increment 3.18, docs/INCREMENT-3.18-REPORT.md). The blob grows from the centre
   * in twin pairs (a half turn through the centre), so both starts see the same land, and the
   * frontier draw is weighted by seeded value noise so the shape has bays and peninsulas and
   * rough ground clusters into ridges. noiseScale is hexes per noise feature; noiseWeight 0 is
   * the old round blob, 1 follows the noise only. Both est.; fixed by the checkpoint
   * screenshots at 380 px and the gates in the report.
   */
  mapShape: { noiseScale: 2.5, noiseWeight: 0.85 },
  /**
   * Unit layer (Increment 4): render constants, not rules. A unit slides between hex centres
   * over the measured move interval, clamped so a paused tab or a 16x dev run stays sane. The
   * gesture ratio is how deep a pointer must sit in a hex to count. All est.; fixed by the
   * Increment 4 checkpoint screenshot at 380 px, DPR 1. The bars are `unitPieces`' since UP1.
   */
  unitLayer: {
    /** A unit's half-height against the hex radius: the figure's frame and the flat silhouette's half (UP1). */
    glyphRatio: 0.42,
    numberFontRatio: 0.5,
    slideMinMs: 40,
    slideMaxMs: 900,
    pathEnterRatio: 0.8,
    /** The order mark's dashes: ring on the destination, dots along the route. */
    routeDotRatio: 0.12,
    /**
     * Controls rework (2026-09-19, owner's phone report: mis-sent units). A unit caught
     * mid-slide is picked by its drawn glyph within this share of the hex radius; a press
     * that travels further than the threshold is a drag, not a tap. Both est., not measured.
     */
    slidingPickRatio: 0.65,
    dragThresholdPx: 10,
    /** The selected unit's ring breathes once per period; an accepted order pings its target. */
    selectionPulseMs: 1100,
    orderPingMs: 450,
    /**
     * The King's aura breathes once per period, slower than the selection ring so the two never
     * read as one; the spark from the crown to a unit picked up inside it lasts this long. Est.
     */
    auraPulseMs: 2600,
    commandSparkMs: 650,
    /** The King's standard flutters once per period (est.), a commander's or leader's banner sways in its own phase (UP1); a soldier's other marks stand still. */
    bannerWaveMs: 1400,
  },
  /**
   * The King's token (Increment 5.20, `src/render/kingShape.ts`), against the hex radius. Alone
   * on a hex he stands low in it and is the tallest piece (a soldier's half-height is
   * `unitLayer.glyphRatio`); beside a unit he steps right and smaller and the unit steps left,
   * scaled down. He breathes with his aura (`unitLayer.auraPulseMs`) by `bobRatio` of his
   * half-height, hops `hopRatio` on a step, and a pointer over him lifts him `hoverLiftRatio`
   * and widens his shadow by `hoverShadowWiden`; a press within `pickSlackPx` of his drawn box
   * takes him. The world view draws a disc under a crown, `worldHeightRatio` of the hex
   * radius. All est., judged by frame captures at 26, 33 and 44 px; nothing here is a rule.
   * `home` (CV1, docs/design/CITY-VISUALS-IDEA.md C.2): alone in a working city of his own he
   * stands in its courtyard as a garrison does, the shared height at the alone place; the
   * mockup's frames showed the alone pose fusing his crown with the capital's and his cape
   * covering the south wall. `sharedUnit` (UP1, docs/design/UNIT-PIECES-IDEA.md): the unit beside
   * him is scaled about its foot, its foot moved (offsetX, offsetY) so it stands behind his, and
   * its faction paints stepped back by `stepBack` (token toward tokenDark 0.78); at 0.75 the
   * soldier's light tunic outweighed his dark cape, and at 0.70 every soldier point stays 0.02 R
   * under his crown at R 22-44 (the shape data's own measure, round 3 of the sketch).
   * `openPickRatio` (CC6): City Command's press takes him anywhere within that share of the hex
   * radius of where he stands, off a hex with an own working city, and beside a soldier only
   * nearer him; measured 2026-10-25 at Play now's 24 px hex, his box took 21x27 px of a 42x48 hex
   * and a press beside him on his own hex took nothing. 0.8 keeps the disc inside his hex
   * (its inner radius 0.87) while he stands. `homeClickSlackRatio` (G47): in a city of his he
   * stands in unpicked, a click without moving within that share of the hex radius of his drawn
   * figure picks him (a drag there is still the city's); the cold start of 2026-10-27 clicked just
   * under his 18x22 px figure at Play now's R 22.5 and took the city. Est., the designer's ruling.
   */
  kingToken: {
    alone: { heightRatio: 0.44, offsetX: 0, offsetY: 0.14 },
    home: { heightRatio: 0.34, offsetX: 0, offsetY: 0.14 },
    shared: { heightRatio: 0.34, offsetX: 0.3, offsetY: 0.14 },
    sharedUnit: { offsetX: -0.22, offsetY: 0.04, scale: 0.7, stepBack: 0.22 },
    bobRatio: 0.04,
    hopRatio: 0.08,
    hoverLiftRatio: 0.06,
    hoverShadowWiden: 0.1,
    pickSlackPx: 3,
    openPickRatio: 0.8,
    homeClickSlackRatio: 0.25,
    worldHeightRatio: 1,
  },
  /**
   * The King's men as painted figures (UP1 of docs/design/UNIT-PIECES-IDEA.md; the shape data in
   * `src/render/unitShape.ts`), against the hex radius unless in px. Figures from `figureMinR`,
   * the flat two-tone silhouette under it (the unit's sign), hands and lit edges from `closeMinR`.
   * They stand on a contact shadow at `shadowAlpha` (the King's is 0.45, so he stays the darkest
   * grounded); a routed unit's faction paints mix `brokenToSlate` toward nobody's slate (2.70:1 the
   * seat's against whole, 2.12:1 a rival's). A strike leans a figure at most `turnCap` radians
   * about its foot (a diamond turned 0.7 read as the siege's square). A figure faces its last
   * blow's target, its last step (turning at the top of the step's first arc, UP2), or the nearest
   * city of another kingdom; a flip needs more than
   * `facingHysteresisRatio` across. A commander's or leader's banner sways `swayRatio` over
   * `unitLayer.bannerWaveMs` in its own phase, `swayingMax` a frame at most (CV5's count). A
   * garrison on a tower hex stands `towerStand` toward its facing and lower, before the rook, and
   * crosses to its other side over `towerTurnMs` when it turns (a pop of 0.24 R before). Bars
   * (`bar`): none at full health at rest; `rest` when wounded (or near the Soldier),
   * `engaged` in a fight, `focus` on the hovered or picked unit, each its width, its height
   * (the larger of the ratio, rounded, and `minPx`) and its track's alpha; their top at `top`, on a
   * city hex `cityTop` and `cityWidth` wide on the ring's south wall, dug in `bankTop` under the
   * bank, and never within `gapPx` of the figure's ink. The health number shrinks to
   * `digitsFloorPx` before it would leave the tile. All est. (the sketch's frames at 22-44 px, not
   * the desk).
   */
  unitPieces: {
    figureMinR: 22,
    closeMinR: 34,
    shadowAlpha: 0.35,
    brokenToSlate: 0.55,
    turnCap: 0.3,
    facingHysteresisRatio: 0.05,
    swayRatio: 0.03,
    swayingMax: 20,
    towerStand: { x: 0.12, y: 0.06 },
    towerTurnMs: 200,
    bar: {
      rest: { width: 0.56, height: 0.08, minPx: 2, blockMinPx: 2, trackAlpha: 0.6 },
      engaged: { width: 0.56, height: 0.1, minPx: 3, blockMinPx: 2, trackAlpha: 0.8 },
      focus: { width: 0.8, height: 0.12, minPx: 3, blockMinPx: 3, trackAlpha: 0.8 },
      top: 0.46,
      cityTop: 0.47,
      cityWidth: 0.44,
      bankTop: 0.52,
      gapPx: 1,
    },
    digitsFloorPx: 9,
    /**
     * UP2, "pieces in hand" (docs/design/UNIT-PIECES-IDEA.md section 7; the ruled table and its
     * clock are `src/render/unitMotion.ts`): the knobs a desk may move, in h (a figure's
     * half-height) or R, all est. from the sketch's sheets, none from the desk. A step lifts
     * `stepLiftH` and leans `stepLeanTurn`, each one `stepStaggerMs` x (its id's hash mod
     * `stepStaggerSlots`) late; a blow knocks its target `knock` of the hex by its luck; a recruit
     * falls from at most `recruitFromRiseH` at `recruitFromScale`; the killer hops `hopRiseH`; the
     * picked-up unit lifts `selectedLiftH`. A flourish played in under `ampNoneMs` of wall time
     * keeps only its flash and fades, one over `ampFullMs` plays whole; a death and a drop last at
     * least `floorMs` of wall time at any speed, a hit's flash and the sword's crescent at least
     * `flashFloorMs` (UP2 review 2: at 8x a hit's 90 ms flash took 11 ms of wall time and most
     * 60 Hz frames missed it; est., 60 ms is three or four frames). Squashes stop at `squashCap` (a
     * piece is rigid).
     * Ink dust: `recruitDots` at a drop, `stepDots` behind a step of the seat's own of
     * `stepDustMinMs` or more, `dotsMax` a frame at most.
     * The lifts are raised from the sketch's 0.2 h and 0.1 h (UP2 review 3: at R 33 a 0.2 h step
     * lift is 2.8 px and a 0.1 h pick-up 1.4 px, so a step read as a flat glide and a picked-up
     * unit never left the board); a held lift of 0.2 h keeps every figure inside its tile.
     */
    motion: {
      squashCap: 0.05,
      stepLiftH: 0.35,
      stepLeanTurn: 0.05,
      stepStaggerMs: 10,
      stepStaggerSlots: 9,
      knock: { normal: 0.11, heavy: 0.16, glancing: 0.07 },
      recruitFromRiseH: 0.7,
      recruitFromScale: 1.08,
      hopRiseH: 0.34,
      selectedLiftH: 0.2,
      ampNoneMs: 60,
      ampFullMs: 200,
      floorMs: 200,
      flashFloorMs: 60,
      recruitDots: 6,
      stepDots: 3,
      stepDustMinMs: 450,
      dotsMax: 30,
    },
    /**
     * UP2.1, the Spear's brace (D-189, docs/design/UNIT-PIECES-IDEA.md section 15): a Spear that has
     * stood still a move interval takes its hold off every blow (`isDugIn`), and shows it. Its spear
     * turns `turn` rad about its hand toward its facing over `setMs` (out-back, the hand's 4 %
     * overshoot), the figure stamps `stomp` of squash at the set's middle and kicks `dots` of ink
     * dust for `dustMs`; stepping off, the spear rises in `liftMs`. A blow on a braced Spear knocks
     * and tilts it `hitShare` of a plain hit's, and rings a white spark of `clangRatio` R on its
     * spear's point for `clangMs` in place of the shield arc. All est., judged in frames at R 28-63
     * (`build/scratch/brace`), none from the desk; `turn` 1.0 is the most the 0.94 tile allows (at
     * 1.1 a Spear at a tower's foot left it at R 22, `test/unitShape.test.ts`).
     */
    brace: {
      turn: 0.9,
      setMs: 240,
      liftMs: 150,
      stomp: 0.04,
      dots: 4,
      dustMs: 200,
      hitShare: 0.35,
      clangMs: 200,
      clangRatio: 0.18,
    },
  },
  /**
   * The moments of `docs/design/MOMENTS-IDEA.md` (MO1, the find, D-160): what plays when the seat's
   * soldier takes a find into its sack, reads its hex with nothing, a find is revealed, or a load is
   * sold. Times in ms at 1x from the change, paced with the tick as a recruit's drop is and lasting
   * at least `unitPieces.motion.floorMs` of wall time; sizes in R, the hex's radius, but the
   * stoop's lean (rad) and dip and the hop (h, a figure's half-height). Sized on the probe
   * `test/sim/moments.sim.ts` (40 seeds: a find taken 1.17 a minute, 35 s apart at the median;
   * a sale 1.03); every number est. until the desk.
   * At most `caps` play at once by tier; one with no room waits `waitMs`, then its state just
   * shows. At most `revealsMax` finds revealed in a tick pop, `reveal.staggerMs` apart. Under
   * reduced motion a moment is a cross-fade of `reducedFadeMs`.
   * MO2 (the piece, D-160's 4.5-4.9): an own city founded falls from `found.fromLift` at
   * `fromScale` in `dropMs`, squashes, throws `dust` ink dots and a ground ring, raises its pennant
   * and washes the land it now pays ring by ring; a tower falls shorter with `chips` of stone; an
   * ally's piece falls as a founding, a rival's from `landed.rivalLift`; a farm cuts its last
   * furrows, a hamlet puffs from its roofs, and one within `murmurGapMs` of the last full one only
   * breathes (the probe: own farms and hamlets 4.14 a minute on Play now, in pairs 2 s apart).
   */
  moments: {
    caps: { loud: 1, deed: 2, murmur: 4 },
    waitMs: 300,
    revealsMax: 6,
    reducedFadeMs: 150,
    take: {
      ringMs: 120,
      ringFrom: 0.8,
      ringTo: 0.22,
      ringAlphaTo: 0.5,
      ringWidthFromPx: 2.5,
      ringWidthToPx: 1.5,
      stoopLean: 0.14,
      stoopDip: 0.05,
      popMs: 60,
      popScale: 1.35,
      sparks: 4,
      sparkMs: 200,
      sparkFrom: 0.12,
      sparkTo: 0.38,
      sparkRadius: 0.025,
      leapMs: 220,
      staggerMs: 60,
      arc: 0.35,
      landScale: 0.7,
      ghostLags: [0.08, 0.16],
      ghostAlphas: [0.35, 0.15],
      sackFrom: 0.6,
      sackOver: 1.15,
      sackMs: 120,
      bounce: 1.08,
      hopH: 0.1,
      hopMs: 140,
      glintWorth: 3,
      glintMs: 140,
      glint: 0.17,
      glintTurn: 0.35,
    },
    bare: { ringAlphaTo: 0.4, ringWidthToPx: 1.2, puffMs: 200, puffSpread: 0.13, dotRadius: 0.025 },
    reveal: { staggerMs: 40, popMs: 160, popScale: 1.25, flash: 0.6 },
    sale: {
      staggerMs: 50,
      leapMs: 200,
      arc: 0.3,
      fromScale: 0.7,
      toScale: 0.9,
      emptyFromMs: 100,
      emptyToMs: 300,
      keepFromMs: 200,
      keepToMs: 420,
      keepAlpha: 0.45,
      keepHalf: 0.18,
      motes: 5,
      moteFromMs: 200,
      moteToMs: 450,
      moteRise: 0.3,
      moteRadius: 0.022,
      floatDelayMs: 200,
    },
    // MO2's probe on Play now (40 seeds, Space every 10 s): at the sketch's 4 s, 3.04 farms, hamlets
    // and others' pieces played whole a minute at the median (the gate: 3 or fewer); at 8 s, 2.37.
    murmurGapMs: 8000,
    found: {
      dropMs: 180,
      fromLift: 0.3,
      fromScale: 1.1,
      fadeInMs: 60,
      shadowAlpha: 0.3,
      squashMs: 120,
      squashY: 0.06,
      squashX: 0.04,
      dust: 8,
      dustMs: 300,
      dustFrom: 0.55,
      dustTo: 0.88,
      dustFlatten: 0.55,
      dustRadius: 0.04,
      dustRadiusTo: 0.015,
      dustAlpha: 0.6,
      ringMs: 320,
      ringFrom: 0.62,
      ringTo: 1,
      ringWidthFromPx: 2,
      ringWidthToPx: 0.5,
      ringAlpha: 0.6,
      pennantFromMs: 260,
      pennantMs: 200,
      washFromMs: 300,
      washStepMs: 70,
      washMs: 220,
      washAlpha: 0.22,
    },
    tower: {
      dropMs: 150,
      fromLift: 0.2,
      fromScale: 1.06,
      squashMs: 100,
      squashY: 0.05,
      squashX: 0.03,
      chips: 5,
      chipMs: 260,
      chipSize: 0.05,
      chipReach: 0.32,
      chipFall: 0.5,
    },
    landed: { rivalDropMs: 160, rivalLift: 0.12, rivalScale: 1.04 },
    farm: {
      strokeMs: 260,
      strokeAlpha: 0.55,
      strokeWidth: 0.05,
      breathFromMs: 200,
      breathMs: 220,
      breathAlpha: 0.08,
    },
    hamlet: {
      breathMs: 220,
      breathAlpha: 0.08,
      puffStaggerMs: 80,
      puffMs: 400,
      puffRise: 0.16,
      puffRiseStep: 0.05,
      // Frames at R 33 (MO2, Edge): at the sketch's 0.02-0.045 R a puff was under 1.5 px and unread.
      puffFrom: 0.035,
      puffTo: 0.07,
      puffAlpha: 0.7,
    },
  },
  /**
   * City Command's screen (Increment CC2, contract S3), against the hex radius unless in px. A
   * working city's pips stand on the bench at its gate and the kind it trains is inked on its
   * keep (`cityLook`, CV1); its arrow is a thick line toward the target with a head and the rally ring's rim; the
   * ghost suggestion is the same arrow dashed in gold. A drop's icon stands over the drop
   * hex. The rival's arrows (since CC4 it points its cities too) are drawn under the seat's own
   * in its colour, at rest only those that threaten the seat's land. A refused drop shakes the piece for shakeMs and shows its reason at the pointer for
   * reasonMs; an arrow about to be replaced blinks once per blinkMs. A key pans keyPanPx. The
   * fixed card's buttons are cardButton px. CC3 (contract S4): a suggestion stands at least
   * adviceHoldMs of wall time before a higher one takes the screen, danger at once (G43: past it
   * a line of its rung or below waits for its end); a city to defend wears a red rim
   * breathing over defendPulseMs and a shield over its keep; a Siege badge sits on an attack's
   * target, adviceBadgeHalfRatio of the hex. Up to chevronMax red chevrons stand chevronInsetPx
   * inside the map's window toward attacks off it, chevronHalfPx each, taking a click within
   * chevronHitPx. The first-run pointer's shape is drawn in units of guideHalfPx (about 1.7 of
   * them tall) and loops every guideLoopMs. quiet-arrows (the owner's "they take all the screen",
   * 2026-10-17): an arrow runs on the line between the two hexes' middles, from arrowStartRatio
   * out of its city's middle to arrowTipRatio short of its target's, so neither keep is covered and
   * arrows onto one target fan out around it (0.72 since CV1: at 0.5 the tip ran into the piece's
   * wall in the mockup's frames); its width stops at arrowMaxWidthPx and its head at
   * arrowMaxHeadPx however far the map zooms in. A quiet arrow is quietWidthShare of the width, a
   * dotted trail, at quietAlpha, its ring at quietRingAlpha (a rival's, a threat to the seat's land,
   * both at rivalArrowAlpha). On the King's own screen (D-202, the owner's "the King remembers his
   * orders", 2026-10-28; D-205, his "only when you give an order", 2026-10-29) an own arrow is
   * drawn only for pressLitMs after an order set it (a drag, a key, a press of the suggestion: G21, the cold start
   * of 2026-10-25 could not see what a press did, which also rings the city it moved), quiet,
   * fading over its last orderFadeMs, and a soldier's order lights its road the same way; none at
   * rest, under the pointer or with its city picked. A soldier's seat sees
   * none of them (D-206, the owner's "for the soldier too", 2026-10-30; K3 drew them quiet). A press on the suggestion's dashed arrow, from its
   * start out to ghostTailRatio of the hex radius and within ghostTailHalfRatio of the line, takes
   * its city (G22: drawn under the King and the purse, the line shows only near the rim, where a
   * press took the ground or a soldier in three cold starts running). A press anywhere on the
   * dashes as drawn, within ghostDashHalfRatio of them and short of the target's hex, takes the
   * city before the King and the soldiers (G22 again: the King's token and the purse cover their
   * first stretch, and the second cold start of 2026-10-26 pressed there and took the King in both
   * runs). A Home with no rival left on or
   * beside its land holds lineGraceMs before any line follows it (G52), and a press of Space with
   * nothing on screen says every city has its order for spaceIdleMs (G23: the cold start of
   * 2026-10-25 lost 3 of 11 presses to lines gone 1-2.5 s before, silently). All est., judged by
   * frame captures at 1280 x 720.
   */
  cityScreen: {
    arrowWidthRatio: 0.13,
    arrowMinWidthPx: 3,
    arrowMaxWidthPx: 6,
    /**
     * G38's casing: the King's road, ridden (solid) or suggested (dashed), is kingRoadWidthRatio
     * of the hex radius between kingRoadMinWidthPx and kingRoadMaxWidthPx, on an opaque dark
     * casing kingRoadCasingPx each side, about 2.5 times a city's arrow at Play now's zoom. The
     * cold start of 2026-10-27 (G38.3) read the ride at the loud arrow's width on a 1.5 px casing
     * as a pale line as wide as the arrows, its edge unseen in 2 of 5 claims. Est., the
     * designer's ruling.
     */
    kingRoadWidthRatio: 0.24,
    kingRoadMinWidthPx: 5,
    kingRoadMaxWidthPx: 9,
    kingRoadCasingPx: 2.5,
    /**
     * G49: the founding line's road dashed in its own widths, a dash kingRoadDashWidths long and a
     * gap kingRoadGapWidths, both as seen past the round caps. At D-187's width the old dashes of
     * 0.2 R closed their 0.2 R gaps and the second cold start of 2026-10-27 read the road solid in
     * 7 of 7 claims. A gap over one of the road's 60-degree bends is seen about 4.0 px wide at 1
     * width (R 22.5), 4.9 px at 1.2 (`test/suggestionGhost.test.ts`, G49). Est.
     */
    kingRoadDashWidths: 2.2,
    kingRoadGapWidths: 1.2,
    arrowHeadRatio: 0.42,
    arrowMaxHeadPx: 16,
    arrowStartRatio: 0.6,
    arrowTipRatio: 0.72,
    arrowAlpha: 0.95,
    quietWidthShare: 0.6,
    quietAlpha: 0.75,
    quietRingAlpha: 0.35,
    rivalArrowAlpha: 0.7,
    previewAlpha: 0.75,
    ringWidthPx: 2.5,
    ghostAlpha: 0.9,
    hoverRimWidthPx: 3,
    dangerAlpha: 0.32,
    ghostKeepAlpha: 0.32,
    markHalfRatio: 0.36,
    blinkMs: 500,
    shakeMs: 360,
    shakeRatio: 0.12,
    shakeTurns: 3,
    reasonMs: 1200,
    reasonHalfPx: 13,
    keyPanPx: 90,
    cardButtonWidthPx: 52,
    cardButtonHeightPx: 56,
    cardGapPx: 6,
    cardMarginPx: 8,
    adviceHoldMs: 3000,
    defendPulseMs: 900,
    adviceBadgeHalfRatio: 0.3,
    chevronMax: 3,
    chevronInsetPx: 26,
    chevronHalfPx: 13,
    chevronHitPx: 24,
    guideLoopMs: 2800,
    guideHalfPx: 18,
    pressLitMs: 2500,
    orderFadeMs: 1000,
    /**
     * G46: a founding the game took from the King (his site lost, or he fled) heads the pill in
     * the echo's place this long, longer than an echo since nothing the player did announced it;
     * Space or a Home ends it sooner. Est., the designer's ruling.
     */
    lostNoticeMs: 4000,
    lineGraceMs: 2000,
    /**
     * G52 (D-194): a Home on screen stands while a rival stands on its city's land or within
     * homeBesideHexes of it and the city keeps its arrow, then lineGraceMs. A rival marches a hex
     * about every 3 s, and each rival of a wave stepping on and off the land's edge was a Home of
     * its own: the second cold start of 2026-10-27 logged Home on, off, on, off in 3 s steps.
     * `test/sim/homeBlink.sim.ts` (200 seeds, Space every 10 s on the line shown): a Home back
     * for its city within 10 s, 16.1 % of Homes before, 1.3 % with it, wins 105 and 106. Est.,
     * the designer's ruling.
     */
    homeBesideHexes: 1,
    spaceIdleMs: 2500,
    ghostTailRatio: 1.3,
    ghostTailHalfRatio: 0.35,
    ghostDashHalfRatio: 0.2,
    /**
     * G35: while a founding suggestion stands, the King's other ghost keeps (his card's choices)
     * are drawn at this share of their alpha, the suggested one whole with its road (est.: the
     * second cold start of 2026-10-26 saw 6-8 equal "20" keeps and could not tell Space's).
     */
    otherSiteShare: 0.4,
    /**
     * G39: a press on no hex within this share of the hex radius past an own working city's edge
     * takes that city (est., D-177: the third cold start of 2026-10-26 pressed the capital's top
     * edge, just off the map's top row, and took nothing). Between two hexes nothing changes.
     */
    cityEdgeSlackRatio: 0.15,
  },
  /**
   * The city and the tower as standing pieces (CV1 of docs/design/CITY-VISUALS-IDEA.md, "Pieces
   * and Purses", C.0 to C.4), against the hex radius unless in px; a `[px, ratio]` pair is the
   * larger of the two at the hex's size. Angles are degrees clockwise from east; wall k faces 60k
   * degrees (0 E, 1 SE, 2 SW, 3 W, 4 NW, 5 NE): walls are lost NE and NW first, the two no unit
   * ever covers, and a city rises from its NE wall. The zoom tiers: the world view's squares under
   * `camera.detailMinHexSizePx`, a plain ring with its keep in the courtyard from there, the map
   * tier from `blockMinHexSizePx`, towers on the joints with a slit and a door from
   * `closeMinHexSizePx`. A press at `pressPoint` takes the city, whoever stands in it; `keepPoint`
   * is the keep's face, where a Home's shield stands. Every value est. until the frames at 26, 33
   * and 44 px; the sketch's section K says why each left its first draft.
   */
  cityLook: {
    blockMinHexSizePx: 18,
    closeMinHexSizePx: 34,
    ringOuter: 0.62,
    ringInner: 0.47,
    blockRingInner: 0.4,
    edgeDrop: 0.09,
    shadow: { x: 0.05, y: 0.11, radius: 0.64, alpha: 0.4 },
    free: { faceToSlate: 0.3, toSlate: 0.2, shadowAlpha: 0.2, edgeDrop: 0.05 },
    courtyardShade: 0.6,
    ownRim: [2, 0.06],
    rim: [1.5, 0.04],
    highlight: { from: 200, to: 250, radius: 0.53 },
    rubble: { radius: 0.585, dips: [0, 0.03, 0.005, 0.035, 0.01, 0.03, 0] },
    cracks: [-14, 11],
    breachAlpha: 0.55,
    keepHalf: 0.18,
    keepTop: -0.68,
    crown: [
      [-0.18, -0.16],
      [-0.18, -0.74],
      [-0.09, -0.64],
      [0, -0.8],
      [0.09, -0.64],
      [0.18, -0.74],
      [0.18, -0.16],
    ],
    face: { y: -0.51, half: 0.1 },
    closeTower: { half: 0.11, radius: 0.585 },
    muster: { radius: 0.7, stepDegrees: 25, spanDegrees: 100, pipHalf: [2.5, 0.09] },
    bench: { inner: 0.6, outer: 0.79, marginDegrees: 14, alpha: 0.85 },
    pausedPipAlpha: 0.4,
    tower: { scale: 1.25, footY: 0.07 },
    wallLossOrder: [5, 4, 0, 3, 1, 2],
    buildOrder: [5, 0, 1, 2, 3, 4],
    keepPoint: { x: 0, y: -0.42 },
    pressPoint: { x: -0.61, y: -0.05 },
    /**
     * The King's purse (CV3, C.3 and D): a pile of `level` gold coins on each own working city,
     * the level from its share of the income (supply an interval) at the cuts, which are est.
     * from script play (p10 6, p50 8, p90 14); a level drops only when the share falls under its
     * cut by more than `coinHysteresis`, and is read again only on a payday, and a pile that moved
     * waits `coinDwellSec` before it moves again: with the hysteresis alone, 79 of 319 level
     * changes came within 30 s of the same city's last (36 back and forth, 43 a city gaining or
     * losing land fast; shortest gap 2 s) on 40 seeds of City Command under the suggestion, and
     * hysteresis 2 or 3 cut the reversals but not the runs; with the dwell none do, at 7.5 % of
     * city-ticks shown off the share's own level against 4.7 % (`test/sim/purse.sim.ts`,
     * docs/INCREMENT-CV3-REPORT.md). `coinsAtRest` false
     * is the undo of the sketch's decision 1 (5.19's "the pointer asks"). The pile stands in the
     * purse column over the west wall, its bottom coin's lower edge at `bottom`, each odd coin
     * `wobble` to the right so it reads as a pile, not a barrel; a coin is `rx` by `ry` with a side
     * `thickness` thick, the pile outlined `edge` px; at the block tier a gold bar a `step` tall
     * a level on `base`; in the world view the city's square is scaled by the level. A payday
     * gleams the piles over `gleamMs`, their faces `gleamTint` toward white at most, a sparkle of
     * arms `sparkle` R. All est. until the desk.
     */
    coinLevelCuts: [6, 9, 12, 16],
    coinHysteresis: 1,
    coinDwellSec: 30,
    coinsAtRest: true,
    purse: {
      x: -0.56,
      bottom: 0.42,
      wobble: 0.025,
      rx: [5, 0.19],
      ry: [2.5, 0.075],
      thickness: [3.5, 0.115],
      edge: 1.5,
      block: { x0: -0.71, x1: -0.51, step: [1.5, 0.09], base: 0.24 },
    },
    worldSquareScales: [0.85, 0.95, 1.05, 1.15, 1.25],
    gleamMs: 250,
    gleamTint: 0.6,
    sparkle: 0.16,
  },
  /**
   * Printed land (CV2 of docs/design/CITY-VISUALS-IDEA.md, C.4 and C.5), in `cityLook`'s units.
   * Owned land no working city of its owner reaches is fallow: its fill mixed `fallowMix` toward
   * nobody's slate, three tufts of grass in that fill at `tuftShade` from the map tier, for every
   * seat (the idle dashes are gone: 96 px² of white an idle hex at 26 px, more than a keep). The
   * prints are drawn in the tile's own colours, no rim and no shadow. A farm is three plots, each
   * one step off the tile (`tone`: a scale under 1, a tint over it), parted by two paths of the
   * plain fill and turned 120 degrees by (q - r) mod 3, so a run of farms is a quilt; its rows of
   * crops at `angle` degrees clockwise from east, `spacing` apart, dashed in columns, only in
   * `mapRowPlots` below the close tier (all three read as woven cloth on a built-up kingdom). A
   * hamlet is three houses in the hex's back half, a well water in a rim, its posts and roof at the
   * close tier. A print being built: a farm's ploughed plots, the first staked out at
   * `unploughedAlpha`; houses and the rim edged at `buildingAlpha`. All est. until the desk, but
   * `hamletTint`, 0.35 (the sketch's 0.42, its fallback in J): the scripted kings build as many
   * hamlets as farms (31 and 33 of 67 own hexes free of pieces and units, 6 seeds at 1:00 and
   * 2:00), and at 0.42 the marks at rest (pixels 1.5:1 off their ground within 0.78 R) rose from
   * 5.2 % to 5.6 % at the fit zoom of 32.7 px; at 0.35 they fall to 4.0 % (6.0 to 4.2 % at
   * 24 px), a hamlet marks 5.9 % of its hex against the old hut's 5.7 %, and its walls are a tone
   * step on orange (1.49:1) under roofs at 2.97:1 (docs/INCREMENT-CV2-REPORT.md).
   */
  landLook: {
    fallowMix: 0.45,
    tuftShade: 0.7,
    tuftWidth: [1.2, 0.035],
    farm: {
      margin: 0.05,
      path: [2.5, 0.085],
      cross: [
        [-0.81, -0.05],
        [0.55, -0.63],
      ],
      side: [
        [-0.1, -0.35],
        [0.41, 0.7],
      ],
      plots: [
        { tone: 0.88, angle: -20, spacing: 0.18 },
        { tone: 1.12, angle: 70, spacing: 0.19 },
        { tone: 0.9, angle: 20, spacing: 0.19 },
      ],
      row: [1, 0.03],
      dash: [3, 0.1],
      gap: [2, 0.06],
      endInset: 0.03,
      mapRowPlots: [1],
      unploughedAlpha: 0.5,
    },
    furrowShade: 0.74,
    hamlet: {
      houses: [
        [0.02, -0.5],
        [-0.27, -0.31],
        [0.25, -0.34],
      ],
      half: 0.11,
      wall: 0.18,
      roof: 0.16,
      roofHalf: 0.145,
      door: 0.03,
      doorHeight: 0.09,
    },
    hamletTint: 0.35,
    hamletRoofShade: 0.5,
    well: { y: -0.48, water: 0.13, rim: 0.195, rimWidth: [2.5, 0.08], closeScale: 1.3 },
    wellRimTint: 0.3,
    wellPostWidth: [2, 0.05],
    buildingAlpha: 0.6,
  },
  /**
   * G15: a land pass that has rested more than `healFrames` frames since it changed is sent to
   * the GPU again as it stands, and again every `rehealFrames` while it rests, on a frame that
   * changed no pass (`render/map/landPasses.ts`). On this host's AMD GPU under ANGLE's D3D11 path
   * a frame of many uploads can leave a pass drawn as wedges until it is uploaded again; a quiet
   * frame's upload heals it, uploads in the busy frame itself drew more wedges, and a pass sent
   * only once could stay wrong (docs/INCREMENT-G15-REPORT.md). est.: a fifth of a second and a
   * second at 60 frames a second.
   */
  landUploads: { healFrames: 12, rehealFrames: 60 },
  /**
   * The end screen says why (Increment CC3, H3 of the cold start): at most `causes` lines, each a
   * cause in game terms, after the line that names what decided where land did (G11). A soldier fell alone with no soldier of its kingdom within `aloneRadius`
   * the tick before (the measure `test/sim/cities.sim.ts` reads); that is a cause once
   * `aloneMinDeaths` fell so and they were at least `aloneShare` of the deaths. In a classic run
   * `unspentSupply` or more left in the pool is one (City Command spends it by itself). All est.
   */
  endScreen: {
    causes: 2,
    aloneRadius: 2,
    aloneMinDeaths: 3,
    aloneShare: 0.5,
    unspentSupply: 200,
  },
  /**
   * Hit and death (Increment 5.8, docs/design/COMBAT-ANIMATION-IDEA.md), timed from the striker's
   * impact since UP2 (`src/render/unitMotion.ts` holds the ruled motion of a strike, a hit, a
   * fall, a drop, a hop and a step). Every value est., none measured. The health a blow took stays
   * pale on the bar for `ghostBarHoldMs` from the impact, then drains over `ghostBarDrainMs`; a
   * dead unit's `chipCount` chips fly `chipFlightRatio` of the hex over `chipMs` from the killing
   * impact (as do the rubble of walls ground down, from the grinder's), and once its body has gone
   * its footprint marks the hex for `deathMarkMs`. All of it
   * shrinks with the dev speed so it fits one combat step.
   */
  combatFx: {
    ghostBarHoldMs: 200,
    ghostBarDrainMs: 300,
    chipMs: 380,
    deathMarkMs: 2000,
    chipCount: 4,
    chipSizeRatio: 0.07,
    chipFlightRatio: 0.55,
    /**
     * Strike lines (ADR-0013 slice 1): who will strike whom, drawn from glyph edge to glyph
     * edge for this long before the combat step lands, red for the enemy's. The attacker coils
     * over the same lead, shortened with the dev speed (UP2). Est.
     */
    strikeLeadMs: 120,
    strikeLineMinPx: 2,
    strikeLineRatio: 0.07,
    /**
     * Attacks per kind (Increment 5.13, slice 2 of the concept), every value est. The attack
     * lasts `attackMs` from the combat step; how far each kind coils, snaps and leans, and when
     * its blow lands, is `unitMotion.ts`'s strike table (UP2). The sword leaves a crescent at the
     * target's edge from its impact; the archer's arrow and the siege's stone leave at their
     * release and fly an arc to their impact while three shout arcs leave the chevron's tip for
     * `shoutMs`; the rider's charge kicks up dust. A flank blow bursts a gold spark on the one
     * struck for `sparkMs` from the impact; a blow on a dug-in or entrenched unit flashes a gold
     * shield arc for `shieldMs`.
     */
    attack: {
      attackMs: 260,
      crescentRatio: 0.34,
      arrowArcRatio: 0.3,
      arrowLengthRatio: 0.22,
      shoutMs: 300,
      shoutArcs: 3,
      stoneArcRatio: 0.7,
      stoneSizeRatio: 0.12,
      dustPuffs: 3,
      dustRatio: 0.09,
      sparkMs: 220,
      sparkRatio: 0.3,
      shieldMs: 180,
      shieldRatio: 0.6,
      /**
       * FL2's luck, est.: a heavy blow bursts a white spark of heavySparkRatio on the one struck,
       * a glancing blow a grey one of glanceSparkRatio, both for sparkMs (its wider coil and longer
       * lunge are the strike table's).
       */
      heavySparkRatio: 0.55,
      glanceSparkRatio: 0.22,
    },
    /**
     * The small joys (Increment 5.13), every value est.: a new veteran sends a gold ring out for
     * `veteranPingMs`; a unit that heals sends two white motes up `healRiseRatio` of the hex
     * for `healMs`; walls ground down throw `wallChipCount` grey chips toward the grinder. The
     * killer's hop is the motion table's (UP2).
     */
    joys: {
      veteranPingMs: 500,
      healMs: 450,
      healRiseRatio: 0.3,
      healMoteRatio: 0.05,
      wallChipCount: 3,
    },
  },
  /**
   * The camera (slice W1, docs/design/WORLD-MAP-IDEA.md). Every value est., none measured: the
   * owner's phone decides. Zoom runs from "the whole map fits" to `maxHexSizePx`. Under
   * `detailMinHexSizePx` the map is the world view: units are dots, cities squares, no bars or
   * numbers, and a tap dives to `diveHexSizePx` there instead of acting. A map whose fit is
   * under the detail floor opens dived in on the player's King. An order drag held within
   * `edgeScrollMarginPx` of the map's edge scrolls it, up to `edgeScrollPxPerSec`.
   */
  camera: {
    maxHexSizePx: 44,
    detailMinHexSizePx: 11,
    diveHexSizePx: 26,
    buttonZoomFactor: 1.5,
    wheelZoomRate: 0.0015,
    wheelMaxDeltaPx: 120,
    edgeScrollMarginPx: 40,
    edgeScrollPxPerSec: 420,
    /**
     * World view marks against the hex radius: a unit's dot, a city's square. The King's crowned
     * disc is `kingToken.worldHeightRatio` (5.20; it replaced a gold ring of 0.7).
     */
    dotRatio: 0.42,
    citySquareRatio: 0.55,
  },
  /**
   * Dev-only controls (DEV builds and VITE_DEBUG_HOOK=1), never in portal builds. Speed
   * multiplies wall-clock time fed to the fixed-step ticker; core ticks stay ticks, so a fast
   * run is the same deterministic run played quicker. The fast preset is for walking the whole
   * loop (start, fight, end screen, restart) in about a minute: the normal rules and clock on a
   * small map at 8x, so it ends the way a normal run ends (elimination, surrender or the
   * clock). 27 hexes since amendment A20 (2026-09-16, docs/INCREMENT-3.11-AMENDMENTS.md): on
   * the symmetric 3.18 map 20 hexes fell to 14/40 decisive ends; 27 is odd (fully paired), idle
   * loses 40/40 and 24/40 end before the clock at a mean of 1017 ticks, about 32 s at 8x.
   */
  /**
   * The front page (B3.1, docs/PLAYTEST-PROTOTYPE-PLAN.md): a visitor's name is generated
   * until they type one, and the day's map is drawn on a plain canvas before the game loads.
   * All est.
   */
  landing: {
    /** A typed name is cut to this; every generated name fits it (`src/front/name.ts`). */
    nameMaxLength: 16,
    /** The day's map keeps this many CSS px clear of its box's edges. */
    heroPaddingPx: 16,
    /** The gap between two tiles of the day's map, in CSS px. */
    heroSeamPx: 2,
    /** Rough ground is drawn at this share of its owner's colour. */
    roughShade: 0.78,
    /**
     * D-120: the longest the map and the duels wait unseen for the lobby server's first word. The
     * playtest page's lobby answered 0.5-0.8 s after the page drew its own map (two loads, the
     * owner's machine, 2026-10-21); twice that, rounded.
     */
    lobbyWaitMs: 1500,
  },
  /**
   * VR3 (D-198): the longest the game waits for the display face before its first title
   * (`src/render/displayFace.ts`). Est.: the front page has fetched the face before Play now in
   * the usual case, and 37 KB take about 0.2 s on 1.5 Mbps.
   */
  displayFace: { waitMs: 1500 },
  /**
   * FP1: the front page's battle (`src/game/showcase.ts`), the map Play now plays with both
   * kingdoms in their scripts' hands. All est., judged in the pane at 1440 x 900.
   */
  showcase: {
    /** The opening played unseen: by 60 s each kingdom has columns out of its capital and land beyond it. */
    warmTicks: 240,
    /** Game time per wall time: fast enough that a visitor sees land change hands, slow enough that pieces still walk. */
    speed: 2,
    /** An ended battle stays on screen this long before it plays again. */
    endHoldMs: 6000,
  },
  /**
   * The front page's duels (B3.2, D-028, D-029): a public duel counts down `countdownSec` once
   * someone sits, reads "Starting…" for `startingMs`, and `listed` duels of each way to play show
   * at once (K2: the company duel's beside the King's duels, each mode on its own countdown;
   * K6.1: two against two after them). A duel's five-character code is its seed text;
   * its seats are its mode's table (`ROOM_TABLES`). All est.
   */
  lobby: {
    countdownSec: 60,
    startingMs: 2000,
    listed: { duel: 3, company: 1, allies: 1, war: 1 },
    roomCodeLength: 5,
    /** How often the cards' countdowns are redrawn. */
    refreshMs: 250,
    /** A card's map (64 CSS px, `index.html`): the space it keeps clear of its edges. */
    thumbPaddingPx: 3,
    /** No gap at this size: a 1 px seam ate most of a 3 px tile and the map read as specks. */
    thumbSeamPx: 0,
    /**
     * A full table starts this long after its last seat is taken (D-029, est.), and since K4.1 a
     * table whose every human pressed Start now this long after the last press (D-104).
     */
    earlyStartMs: 3000,
    /** The page keeps its local duels when the lobby server has not answered in this long (est.). */
    connectTimeoutMs: 3000,
    /** Waits between the front page's tries to reach the lobby again, then the last one (B3.7, est.). */
    lobbyRetryMs: [2000, 4000, 8000, 15000],
    /** `wrangler dev`'s address: dev and e2e builds reach it with `?lobby=server` (B3.5). */
    devServerUrl: "ws://127.0.0.1:8787",
  },
  /**
   * The relay (B3.4, ADR-0016, D-023 to D-027): what a room accepts from one connection and how
   * often a client reports the hash of its state. All est.
   */
  net: {
    /** A message longer than this (characters of its JSON) is refused. */
    maxMessageChars: 4096,
    /** Commands a seat may send for one turn (ADR-0014's number). */
    commandsPerTurn: 8,
    /** A client hashes its state every this many ticks and tells the room (2 s at speed 1). */
    hashEveryTicks: 8,
    /** A turn this long after the one before held the page at its tick (1.5 turns, B3.6, est.). */
    lateTurnMs: 375,
    /** A page in a match times the room's socket this often (B3.6, est.). */
    pingEveryMs: 2000,
    /** The most turns a page takes at once when it catches up; the rest wait for the next moment. */
    catchUpTicks: 40,
    /** A page keeps this many of its last reported hashes, for two pages to compare (B3.7). */
    hashesKept: 64,
    /** Messages of every kind one connection may send a room in one turn (B3.7, est.). */
    messagesPerTurn: 24,
    /** Turns over that budget after which the room closes the connection (B3.7, est.). */
    strikesToClose: 3,
    /**
     * Sockets one room accepts at once, by its mode: the duel's two seats and their reconnects
     * with room to spare; the company's ten seats and two more (K2, the sketch's seats plus two);
     * two against two's four Kings, the duel's 8 (K6.1); the war's twenty Kings and four more
     * (K7.1). All est.
     */
    socketsPerRoom: { duel: 8, company: 12, allies: 8, war: 24 },
    /** A human whose socket dropped keeps the seat this many turns before its script takes it (10 s, est.). */
    graceTurns: 40,
    /** A page that hears nothing from its room this long counts the socket as dropped (B3.7, est.). */
    silentMs: 5000,
    /** Waits between a dropped page's tries to come back, then the last one again (B3.7, est.). */
    retryMs: [500, 1000, 2000, 4000],
    /** A page that has not come back in this long plays on alone, against scripts (the kill switch, est.). */
    giveUpMs: 15000,
    /**
     * K4: the room keeps a snapshot every this many ticks (1 min at speed 1, the sketch's est.):
     * a joiner under way replays at most this many turns from it.
     */
    snapshotEveryTicks: 240,
    /**
     * K4: the longest snapshot a room takes, and only from the page it asked. A company duel's
     * state measured 23,742 to 27,713 characters at tick 480 (6 seeds, `test/snapshot.test.ts`);
     * K7.1: the war's 131 K to 169 K through its twenty minutes (3 seeds, `test/sim/warRoom.sim.ts`),
     * so 256 K, about half again over the war's most.
     */
    maxSnapshotChars: 262_144,
    /** K4: a page keeps the text of its last this many reported hashes, for the room's offer. */
    snapshotTextsKept: 4,
  },
  /**
   * The playtest's events (B3.9, the plan's analytics tree): how a page batches what it sends
   * and what the server takes in one batch. All est.
   */
  events: {
    /** A page sends its batch when it holds this many events, */
    maxEvents: 25,
    /** or this often while it holds any, and when it is hidden or closed. */
    everyMs: 5000,
    /** A batch longer than this (characters of its JSON) is refused. */
    maxBatchChars: 16384,
    /** Fields one event may carry. */
    maxDataKeys: 12,
    /** The longest word an event may carry (an error's message is cut to it). */
    maxTextChars: 120,
    /** B3.10: what a player writes in the feedback line or a bug report, at most. */
    maxFeedbackChars: 500,
    /** AN1: the longest referrer host and campaign word a landing carries. */
    maxSourceChars: 64,
  },
  /**
   * B3.10, the plan's end screen: the feedback prompt comes after the `firstAfter`-th match a tab
   * ends, then after every `every`-th one. Est.
   */
  feedback: {
    firstAfter: 2,
    every: 2,
  },
  /**
   * B3.10.1, Google sign-in by redirect (D-077): the page's wait for the Worker, and what the
   * Worker takes and keeps. All est.
   */
  signIn: {
    /** The page waits this long for the Worker's answer to Google's token, then stays a guest. */
    answerTimeoutMs: 8000,
    /** Random bytes of the request's state and of its nonce. */
    randomBytes: 16,
    /** A request to `/signin` longer than this (characters of its JSON) is refused. */
    maxRequestChars: 4096,
    /** Google's clock and the Worker's may differ by this much on a token's times. */
    clockSkewSec: 300,
    /** Google's keys are kept this long when its answer names no max-age. */
    keysMaxAgeSec: 3600,
    /** A token under a key the Worker does not hold fetches Google's keys again, at most this often. */
    keysRefetchMs: 60_000,
  },
  /**
   * NM2, names owned by accounts (D-110): what the Worker takes and how long a session seals an
   * account's claims, and how the page asks. All est.
   */
  names: {
    /** A request to `/names` longer than this (characters of its JSON) is refused. */
    maxRequestChars: 512,
    /** A signed-in page's session runs out after this many days; then it signs in again. */
    sessionDays: 180,
    /** The page waits this long for the Worker's answer, then keeps the name it had. */
    answerTimeoutMs: 5000,
    /** The dice roll again this many times while another account holds the rolled name. */
    rollTries: 6,
  },
  /**
   * AP1, what an account has earned kept with it (D-112): what the Worker takes, and how long the
   * page waits. All est.
   */
  points: {
    /** A request to `/points` longer than this (characters of its JSON) is refused. */
    maxRequestChars: 512,
    /** A count above this is no count a run could bank: the request is refused. */
    maxCount: 10_000_000,
    /** The page waits this long for the Worker's answer, then plays on the bank it holds. */
    answerTimeoutMs: 5000,
  },
  dev: {
    speeds: [1, 2, 4, 8, 16],
    fastMapHexCount: 27,
    fastSpeed: 8,
    /**
     * The camera's test bed until the world map exists (slice W1): a procgen duel on a map too
     * big to read whole. The disc the blob grows in holds this many times the hexes, about the
     * slack of the shipped map (91 for 40 to 60). Both est.
     */
    bigMapHexCount: 301,
    bigMapDiscSlack: 1.5,
  },
} as const;
